import { useCallback, useState, type ReactNode } from 'react';
import { GetAppQr } from '~/components/ui/GetAppQr';
import { AppModalContext } from '~/lib/appModal';
import { cn } from '~/lib/cn';
import { useContent } from '~/lib/useLanguage';
import { ModalShell } from './ModalShell';

/**
 * Provides the app-download QR modal to the whole page. Desktop "Get the app"
 * triggers (see ButtonLink) open it via the AppModalContext; mobile CTAs skip
 * this and go straight to the store. The QR encodes the current host's
 * `/get-the-app` (UA redirect). The modal rises from the bottom over a shared
 * blur backdrop (matching the mega menu).
 */
export function AppDownloadProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => setOpen(false), []);

  return (
    <AppModalContext.Provider value={openModal}>
      {children}
      <AppDownloadModal open={open} onClose={closeModal} />
    </AppModalContext.Provider>
  );
}

function AppDownloadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Opened by the header CTA, so it inherits whatever language that page is in.
  const { navigation } = useContent();

  return (
    <ModalShell open={open} onClose={onClose} label={navigation.getAppCta.label}>
      <div className="flex w-60 flex-col items-center gap-10 rounded-[32px] bg-white px-8 pt-10 pb-8">
        {/* Title + QR fade out faster than the card's slide/fade, so the
            contents blend out first for a cleaner close. */}
        <p
          className={cn(
            'text-h4 text-ink transition-opacity duration-200 ease-nav',
            open ? 'opacity-100' : 'opacity-0',
          )}>
          {navigation.getAppCta.label}
        </p>
        <GetAppQr
          className={cn(
            'w-full transition-opacity duration-200 ease-nav',
            open ? 'opacity-100' : 'opacity-0',
          )}
        />
      </div>
    </ModalShell>
  );
}
